import { HttpClient } from '@angular/common/http';
import { effect, Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { environment } from '@environments/environment';
import { ExistingInsuranceResponse } from '@models/api-calls/application/application-response.model';
import { ApplicationInformationSubSteps, MainSteps } from '@models/config/app-structure.model';
import { InsuranceCompanyListFormControls } from '@models/form/form.model';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationService } from '@services/api-calls/application.service';
import { ErrorHandlingService } from '@services/error-handling.service';
import { ApplicationFormService } from '@services/form/application-form.service';
import { ExistingInsuranceSubstepFormEnum, InsuranceCompanyListFormEnum } from 'app/enums/form/form.enum';
import { catchError, Observable, tap } from 'rxjs';

const API_URL = environment.apiUrl;

export enum InsuranceType {
    EXISTING = 'existing',
    PENDING = 'pending',
}

@Injectable({
    providedIn: 'root',
})
export class ExistingInsuranceService {
    constructor(
        private http: HttpClient,
        private applicationService: ApplicationService,
        private applicationFormService: ApplicationFormService,
        private errorHandlingService: ErrorHandlingService,
        private translateService: TranslateService,
    ) {
        effect(() => {
            const shouldShowList = this.applicationService.showInsuranceCompaniesList();
            if (shouldShowList) {
                this.patchInsuranceCompaniesDataOnForm();
            }
        });
    }

    saveAllInsuranceCompanies(): Observable<unknown> | undefined {
        const applicationId = this.applicationService.application()?.id;

        if (!applicationId) {
            return this.errorHandlingService.handleSaveError(new Error(this.translateService.instant('ERRORS.MISSING_APPLICATION_DATA')));
        }

        const insuranceArray = this.getInsuranceCompaniesFormArray();

        if (!insuranceArray || insuranceArray.length === 0 || insuranceArray.invalid) {
            return;
        }

        // Check if there are actual changes
        const hasChanges = this.hasChangesComparedToServer();

        if (!hasChanges) {
            return;
        }

        // Build payload
        const insuranceCompanies = this.buildInsuranceCompaniesPayload();

        return this.updateAllInsuranceCompanies(applicationId, insuranceCompanies).pipe(
            tap(() => {
                this.applicationService.refreshApplicationData.update((n) => n + 1);
            }),
            catchError((error) => this.errorHandlingService.handleSaveError(error)),
        );
    }

    deleteAllInsuranceCompanies(options?: { silent?: boolean }): Observable<unknown> | undefined {
        const applicationId = this.applicationService.application()?.id;
        if (!applicationId) return undefined;

        const serverInsurances = this.applicationService.rawApplication()?.app_existing_insurances;
        if (!serverInsurances?.length) return undefined;

        const obs = this.updateAllInsuranceCompanies(applicationId, []);

        if (options?.silent) {
            return obs.pipe(catchError((error) => this.errorHandlingService.handleSaveError(error)));
        }

        return obs.pipe(
            tap(() => {
                this.applicationService.refreshApplicationData.update((n) => n + 1);
            }),
            catchError((error) => this.errorHandlingService.handleSaveError(error)),
        );
    }

    patchInsuranceCompaniesDataOnForm(): void {
        const existingInsurances: ExistingInsuranceResponse[] = this.applicationService.rawApplication()?.app_existing_insurances || [];
        if (!existingInsurances.length) return;

        const insuranceArray = this.getInsuranceCompaniesFormArray();
        if (!insuranceArray) return;

        // Skip if form already matches server data — prevents infinite effect loop
        // between this effect and the insuranceCompaniesList effect in ApplicationService
        if (this.formMatchesServerData(insuranceArray, existingInsurances)) return;

        // Clear existing form array
        while (insuranceArray.length > 0) {
            insuranceArray.removeAt(0);
        }

        // Populate form array with server data
        existingInsurances.forEach((insurance) => {
            const formGroup = this.applicationFormService.createInsuranceCompanyListItemForm();

            formGroup.get(InsuranceCompanyListFormEnum.ID)?.patchValue(insurance.id);
            formGroup.get(InsuranceCompanyListFormEnum.INSURANCE_COMPANY)?.patchValue(insurance.insurance_company);
            formGroup.get(InsuranceCompanyListFormEnum.POLICY_NUMBER)?.patchValue(insurance.policy_sn);
            formGroup.get(InsuranceCompanyListFormEnum.FACE_AMOUNT)?.patchValue(insurance.face_amount);
            formGroup.get(InsuranceCompanyListFormEnum.UNKNOWN_COMPANY)?.patchValue(insurance.insurance_company === 'Unknown Company');

            insuranceArray.push(formGroup);
        });

        // Mark as pristine to match server state
        insuranceArray.markAsPristine();
        insuranceArray.markAsUntouched();
    }

    private formMatchesServerData(
        insuranceArray: FormArray<FormGroup<InsuranceCompanyListFormControls>>,
        serverInsurances: ExistingInsuranceResponse[],
    ): boolean {
        if (insuranceArray.length !== serverInsurances.length) return false;

        for (let i = 0; i < insuranceArray.length; i++) {
            const formData = this.createInsuranceDataFromForm(insuranceArray.at(i));
            const serverData = serverInsurances[i];

            if (
                formData.id !== serverData.id ||
                formData.insurance_company !== serverData.insurance_company ||
                formData.policy_sn !== serverData.policy_sn ||
                formData.face_amount !== serverData.face_amount
            ) {
                return false;
            }
        }

        return true;
    }

    hasChangesComparedToServer(): boolean {
        const insuranceArray = this.getInsuranceCompaniesFormArray();
        if (!insuranceArray) return false;

        const formInsurances = insuranceArray.controls.map((formGroup) => this.createInsuranceDataFromForm(formGroup));
        const serverInsurances = this.applicationService.rawApplication()?.app_existing_insurances || [];

        // Different count = changes
        if (formInsurances.length !== serverInsurances.length) return true;

        // Compare each insurance
        for (let i = 0; i < formInsurances.length; i++) {
            const formIns = formInsurances[i];
            const serverId = formIns.id;

            if (!serverId) {
                // New insurance (no ID) = changes
                return true;
            }

            const serverIns = serverInsurances.find((ins) => ins.id === serverId);
            if (!serverIns) {
                // ID exists in form but not on server = changes
                return true;
            }

            // Compare field by field
            if (this.insuranceHasChanges(formIns, serverIns)) {
                return true;
            }
        }

        return false;
    }

    private insuranceHasChanges(formData: Partial<ExistingInsuranceResponse>, serverData: ExistingInsuranceResponse): boolean {
        return (
            formData.insurance_company !== serverData.insurance_company ||
            formData.policy_sn !== serverData.policy_sn ||
            formData.face_amount !== serverData.face_amount
        );
    }

    private buildInsuranceCompaniesPayload(): Partial<ExistingInsuranceResponse>[] {
        const insuranceArray = this.getInsuranceCompaniesFormArray();
        if (!insuranceArray) return [];

        return insuranceArray.controls.map((formGroup) => this.createInsuranceDataFromForm(formGroup));
    }

    private createInsuranceDataFromForm(formGroup: FormGroup<InsuranceCompanyListFormControls>): Partial<ExistingInsuranceResponse> {
        return {
            id: formGroup.get(InsuranceCompanyListFormEnum.ID)?.value || null,
            insurance_company: formGroup.get(InsuranceCompanyListFormEnum.INSURANCE_COMPANY)?.value || '',
            policy_sn: formGroup.get(InsuranceCompanyListFormEnum.POLICY_NUMBER)?.value || null,
            face_amount: formGroup.get(InsuranceCompanyListFormEnum.FACE_AMOUNT)?.value || null,
        };
    }

    private getInsuranceCompaniesFormArray(): FormArray<FormGroup<InsuranceCompanyListFormControls>> | null {
        const array = this.applicationFormService.applicationForm.get([
            MainSteps.APPLICATION_INFORMATION,
            ApplicationInformationSubSteps.EXISTING_INSURANCE_REPLACEMENT,
            ExistingInsuranceSubstepFormEnum.INSURANCE_COMPANIES_LIST,
        ]);

        return array as FormArray<FormGroup<InsuranceCompanyListFormControls>> | null;
    }

    private updateAllInsuranceCompanies(
        applicationId: number,
        insuranceCompanies: Partial<ExistingInsuranceResponse>[],
    ): Observable<ExistingInsuranceResponse[]> {
        const url = `${API_URL}/applications/${applicationId}/insurances`;
        return this.http.put<ExistingInsuranceResponse[]>(url, {
            insurance_type: InsuranceType.EXISTING,
            items: insuranceCompanies,
        });
    }
}
